// Panneaux repliables de l'interface en partie (demande de Léni : sur téléphone, l'écran était presque entièrement
// couvert). Un panneau fermé ne montre qu'une ligne (titre + résumé) ; ouvert, son contenu. L'état est retenu par
// appareil (tactile ou souris) dans le navigateur ; sur tactile, un groupe se comporte en accordéon.
import { useCallback, useState, type ReactNode } from 'react';
import { t } from '../i18n';
import { Icon } from './icons';

const KEY = 'tankshift.hud.v1';

function load(): Record<string, boolean> {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

function save(all: Record<string, boolean>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* navigation privée : l'état ne sera simplement pas retenu */
  }
}

/**
 * États ouverts d'un groupe de panneaux. `defaults` donne l'état initial de chacun ; `accordion` : en ouvrir un
 * referme les autres du groupe (tactile).
 */
export function useHudPanels<K extends string>(touch: boolean, defaults: Record<K, boolean>, accordion = false): [Record<K, boolean>, (id: K) => void] {
  const dev = touch ? 't' : 'd';
  const [open, setOpen] = useState<Record<K, boolean>>(() => {
    const saved = load();
    const out = { ...defaults };
    for (const id of Object.keys(defaults) as K[]) if (typeof saved[`${dev}:${id}`] === 'boolean') out[id] = saved[`${dev}:${id}`];
    if (accordion) {
      // Un seul ouvert au plus, même si l'état retenu en ouvrait plusieurs.
      let seen = false;
      for (const id of Object.keys(out) as K[]) {
        if (out[id] && seen) out[id] = false;
        if (out[id]) seen = true;
      }
    }
    return out;
  });
  const toggle = useCallback(
    (id: K) =>
      setOpen((cur) => {
        const next = { ...cur, [id]: !cur[id] } as Record<K, boolean>;
        if (accordion && next[id]) for (const k of Object.keys(next) as K[]) if (k !== id) next[k] = false;
        const all = load();
        for (const k of Object.keys(next) as K[]) all[`${dev}:${k}`] = next[k];
        save(all);
        return next;
      }),
    [dev, accordion],
  );
  return [open, toggle];
}

export function HudPanel({
  open,
  onToggle,
  title,
  summary,
  className = '',
  testId,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  /** Résumé d'une ligne, affiché à côté du titre (surtout utile panneau fermé). */
  summary?: ReactNode;
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <section className={`hud-panel ${open ? 'open' : 'closed'} ${className}`} data-testid={testId}>
      <button type="button" className="hud-panel-head" aria-expanded={open} aria-label={t(open ? 'panelHide' : 'panelShow', { name: title })} onClick={onToggle}>
        <span className="hud-panel-title">{title}</span>
        {summary !== undefined && summary !== null && summary !== '' && <span className="hud-panel-summary">{summary}</span>}
        <Icon name={open ? 'up' : 'down'} size={12} className="icon hud-panel-chevron" />
      </button>
      {open && <div className="hud-panel-body">{children}</div>}
    </section>
  );
}
